import { useState, type ReactNode } from "react"
import {
  Link,
  createFileRoute,
  useCanGoBack,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router"
import { Award, Box, Clock, Users } from "lucide-react"

import { AdvisorHomeHero } from "@/components/home/advisor-home-hero"
import { formatMinuten } from "@/components/idd/idd-format"
import { IddHinweisDialog } from "@/components/idd/idd-hinweis-dialog"
import { IddKachel } from "@/components/idd/idd-kachel"
import { IDD_MODULE, iddInhaltVon, iddStandVon, type IddModul } from "@/components/idd/idd-module"
import { LoadState } from "@/components/load-state"
import { ContentGrid } from "@/components/media-card"
import { PageContainer } from "@/components/page-container"
import { StatCard } from "@/components/stat-card"
import { useIddInhalte, useIddUebersicht } from "@/lib/idd-queries"
import { useHomeIdentity } from "@/lib/use-advisor-home-data"

const BESTAETIGT_KEY = "nl.idd.hinweisBestaetigt"

function leseBestaetigt(): boolean {
  try {
    return sessionStorage.getItem(BESTAETIGT_KEY) === "1"
  } catch {
    return false
  }
}

function merkeBestaetigt() {
  try {
    sessionStorage.setItem(BESTAETIGT_KEY, "1")
  } catch {
    // Without storage the notice simply shows again on the next visit.
  }
}

function Abschnitt({
  id,
  icon,
  titel,
  beschreibung,
  module,
  children,
}: {
  id: string
  icon: ReactNode
  titel: string
  beschreibung: string
  module: IddModul[]
  children: (modul: IddModul) => ReactNode
}) {
  return (
    <section aria-labelledby={id}>
      <div className="section-header">
        <div className="section-heading items-center">
          <div className="flex self-center text-primary" aria-hidden>
            {icon}
          </div>
          <h2 id={id}>{titel}</h2>
          <span>{beschreibung}</span>
        </div>
      </div>
      <ContentGrid>{module.map(children)}</ContentGrid>
    </section>
  )
}

function IddPage() {
  const identity = useHomeIdentity()
  const navigate = useNavigate()
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const ausNavigation = useRouterState({ select: (s) => s.location.state.iddHinweis === true })
  const [bestaetigt, setBestaetigt] = useState(leseBestaetigt)
  const uebersicht = useIddUebersicht()
  const { data: inhalte } = useIddInhalte()
  const daten = uebersicht.data

  const weiter = () => {
    merkeBestaetigt()
    setBestaetigt(true)
    void navigate({ to: "/idd", replace: true, state: {} })
  }
  const abbrechen = () => {
    if (canGoBack) router.history.back()
    else void navigate({ to: "/" })
  }

  const kachel = (modul: IddModul) => (
    <IddKachel
      key={`${modul.art}-${modul.key}`}
      modul={modul}
      inhalt={iddInhaltVon(inhalte, modul)}
      stand={iddStandVon(daten?.module, modul)}
    />
  )

  return (
    <>
      <AdvisorHomeHero
        identity={identity}
        beschreibung="Stärke deine Beratungskompetenz und sammle IDD-Zeit mit realistischen Trainings und Gesprächssimulationen."
        platzhalter="Was möchtest du heute trainieren?"
        onStart={(frage) => void navigate({ to: "/chat", search: { frage } })}
        kennzahlen={
          <>
            <StatCard
              value={daten ? formatMinuten(daten.minutenImJahr) : undefined}
              label={daten ? `IDD-Zeit ${daten.jahr} gesammelt` : "IDD-Zeit gesammelt"}
              icon={<Clock size={16} />}
            />
            <Link to="/idd/zertifikate" className="stat-card stat-card-link">
              <Award size={22} className="shrink-0 text-primary" aria-hidden />
              <span className="stat-card-body">
                <span className="text-[15px] font-medium">Zertifikate</span>
                <span className="stat-card-label">Übersicht öffnen</span>
              </span>
            </Link>
          </>
        }
      />
      <PageContainer width="library" className="library-sections">
        {uebersicht.isError && (
          <LoadState failed onRetry={() => void uebersicht.refetch()}>
            Dein IDD-Stand konnte nicht geladen werden.
          </LoadState>
        )}
        <Abschnitt
          id="idd-gespraech"
          icon={<Users size={22} />}
          titel="Gesprächstraining"
          beschreibung="Praxisnah üben und Beratungssituationen sicher meistern."
          module={IDD_MODULE.filter((m) => m.art === "gespraech")}
        >
          {kachel}
        </Abschnitt>
        <Abschnitt
          id="idd-produkt"
          icon={<Box size={22} />}
          titel="Produkttraining"
          beschreibung="Unsere Produkte kennen, sicher beraten."
          module={IDD_MODULE.filter((m) => m.art === "produkt")}
        >
          {kachel}
        </Abschnitt>
      </PageContainer>
      <IddHinweisDialog open={ausNavigation || !bestaetigt} onWeiter={weiter} onAbbrechen={abbrechen} />
    </>
  )
}

export const Route = createFileRoute("/_app/idd/")({
  component: IddPage,
})
